// Validation helpers for signatory-filled editable fields (see EditableField.format).
// IBAN validation uses ISO 13616 (mod-97) plus a per-country length check.
// No external library is used to keep the bundle small.

import type { EditableField } from '@/components/SignDocumentCommon/types'

export const isIbanField = (field: EditableField): boolean => field.format.type === 'iban'

export const isNumberField = (field: EditableField): boolean => field.format.type === 'number'

/** Digits-only validation for `number` fields. */
export const isValidNumber = (value: string): boolean => /^\d+$/.test(value.trim())

// Official IBAN length per country (ISO 3166-1 alpha-2 country code).
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SC: 31,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
}

/** Removes spaces and upper-cases, matching how users tend to type an IBAN. */
export const normalizeIban = (value: string): string => value.replace(/\s+/g, '').toUpperCase()

export const isValidIban = (value: string): boolean => {
  const iban = normalizeIban(value)

  if (!/^[A-Z0-9]+$/.test(iban)) return false

  const countryCode = iban.slice(0, 2)
  const expectedLength = IBAN_LENGTHS[countryCode]
  if (!expectedLength || iban.length !== expectedLength) return false

  // Move the first four chars to the end, then convert letters to numbers (A=10..Z=35).
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  const numeric = rearranged.replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString())

  // Piecewise mod-97 to avoid BigInt / overflow on long strings.
  let remainder = 0
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = Number(String(remainder) + numeric.slice(i, i + 7)) % 97
  }

  return remainder === 1
}
